import { AvField, AvForm } from "availity-reactstrap-validation";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bounce, ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { Button, Card, CardBody, Col, Label, Row } from "reactstrap";
import api from "../../services/api";

const EditRefund = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  document.title = "Technical Hub | Edit Refund";

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const backendFileUrl = process.env.REACT_APP_BACKEND_IMAGE_URL;

  // State for form fields
  const [formData, setFormData] = useState({
    roll_no: "",
    name: "",
    course: "",
    technology: "",
    refund_amount: "",
    refund_reason: "",
    term: "",
    payment_id: "",
    payment_date: "",
  });

  const [file, setFile] = useState(null);
  const [existingFile, setExistingFile] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getRefundData();
  }, [id]);

  // Fetch existing refund data by ID
  const getRefundData = async () => {
    try {
      setLoading(true);
      console.log("Fetching refund data for ID:", id);

      const response = await api.get(
        `${backendUrl}get-refund-by-id/${id}`
      );
      console.log(id);
      console.log("Full API Response:", response);
      console.log("Response Data:", response.data);

      if (response.status === 200) {
        const data = response.data;

        // Pre-fill form with existing data
        setFormData({
          roll_no: data.data.roll_no || "",
          name: data.data.name || "",
          course: data.data.course || "",
          technology: data.data.technology || "",
          refund_amount: data.data.refund_amount ? data.data.refund_amount.toString() : "",
          refund_reason: data.data.refund_reason || "",
          term: data.data.term || "",
          payment_id: data.data.payment_id || "",
          payment_date: data.data.created_at ? new Date(data.data.created_at).toISOString().split('T')[0] : "",
        });
        console.log(data.data.roll_no);



        // Set existing file path
        if (data.data.file) {
          setExistingFile(data.data.file);
        }

        console.log("Form data set successfully");
      }
    } catch (err) {
      console.error("Error fetching refund data:", err);
      if (err.response) {
        console.error("Server response:", err.response.data);
        console.error("Status:", err.response.status);
        toast.error(`Error: ${err.response.data.message || "Failed to load data"}`);
      } else if (err.request) {
        console.error("No response received:", err.request);
        toast.error("Network error: Unable to connect to server");
      } else {
        console.error("Error:", err.message);
        toast.error("Error loading refund data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle file change
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (5MB limit)
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }

      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error("Only JPEG, PNG, and PDF files are allowed");
        return;
      }

      setFile(selectedFile);
    }
  };

  // Handle form submission
  const handleSubmit = async (event, values) => {
    event.preventDefault();

    // Basic validation
    if (!formData.roll_no || !formData.name || !formData.refund_amount) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);

    try {
      // Create FormData object for file upload
      const submitData = new FormData();

      // Append all form fields
      submitData.append('roll_no', formData.roll_no);
      submitData.append('name', formData.name);
      submitData.append('course', formData.course);
      submitData.append('technology', formData.technology);
      submitData.append('refund_amount', formData.refund_amount);
      submitData.append('refund_reason', formData.refund_reason);
      submitData.append('term', formData.term);
      submitData.append('payment_id', formData.payment_id);
      submitData.append('payment_date', formData.payment_date);

      // Append file if selected
      if (file) {
        submitData.append('file', file);
      }

      console.log("Submitting refund data...");

      const response = await api.put(
        `${backendUrl}update-refund/${id}`,
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        toast.success("Refund updated successfully!");
        setTimeout(() => {
          navigate("/course-refunds-list"); // Adjust the route as per your application
        }, 2000);
      }
    } catch (error) {
      console.error("Error updating refund:", error);
      if (error.response) {
        console.error("Server response:", error.response.data);
        toast.error(`Error: ${error.response.data.message || "Please try again."}`);
      } else {
        toast.error("Error updating refund. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Format file name for display
  const getFileName = (filePath) => {
    if (!filePath) return "";
    return filePath.split('/').pop();
  };

  // Show loading state while data is being fetched
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="ms-2">Loading refund data...</span>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Row>
        <ToastContainer
          position="top-right"
          autoClose={1000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          transition={Bounce}
        />
        <Col xl="12">
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="card-title mb-0">Edit Refund Data</h4>
                <Button color="secondary" size="sm" onClick={() => navigate("/course-refunds-list")}>
                  Back to List
                </Button>
              </div>

              <AvForm className="needs-validation" onValidSubmit={handleSubmit}>
                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="roll_no" className="form-label">
                        Roll Number *
                      </Label>
                      <AvField
                        name="roll_no"
                        value={formData.roll_no}
                        onChange={handleInputChange}
                        placeholder="Enter Roll Number"
                        type="text"
                        className="form-control"
                        id="roll_no"

                      />
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="name" className="form-label">
                        Name *
                      </Label>
                      <AvField
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter Name"
                        type="text"
                        className="form-control"
                        id="name"

                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="course_name" className="form-label">
                        Course
                      </Label>
                      <AvField
                        name="course_name"
                        value={formData.course}
                        onChange={handleInputChange}
                        placeholder="Enter Course Name"
                        type="text"
                        className="form-control"
                        id="course_name"
                      />
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="program_name" className="form-label">
                        Technology
                      </Label>
                      <AvField
                        name="program_name"
                        value={formData.technology}
                        onChange={handleInputChange}
                        placeholder="Enter Program Name"
                        type="text"
                        className="form-control"
                        id="program_name"
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="refund_amount" className="form-label">
                        Refund Amount *
                      </Label>
                      <AvField
                        name="refund_amount"
                        value={formData.refund_amount}
                        onChange={handleInputChange}
                        placeholder="Enter Refund Amount"
                        type="number"
                        className="form-control"
                        id="refund_amount"

                      />
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="term" className="form-label">
                        Term
                      </Label>
                      <AvField
                        name="term"
                        value={formData.term}
                        onChange={handleInputChange}
                        placeholder="Enter Term"
                        type="text"
                        className="form-control"
                        id="term"
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="payment_id" className="form-label">
                        Payment ID
                      </Label>
                      <AvField
                        name="payment_id"
                        value={formData.payment_id}
                        onChange={handleInputChange}
                        placeholder="Enter Payment ID"
                        type="text"
                        className="form-control"
                        id="payment_id"
                      />
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="payment_date" className="form-label">
                        Payment Date
                      </Label>
                      <AvField
                        name="payment_date"
                        value={formData.payment_date}
                        onChange={handleInputChange}
                        type="date"
                        className="form-control"
                        id="payment_date"
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="12">
                    <div className="mb-3">
                      <Label htmlFor="refund_reason" className="form-label">
                        Refund Reason
                      </Label>
                      <AvField
                        name="refund_reason"
                        value={formData.refund_reason}
                        onChange={handleInputChange}
                        placeholder="Enter Refund Reason"
                        type="textarea"
                        className="form-control"
                        id="refund_reason"
                        rows="3"
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="12">
                    <div className="mb-3">
                      <Label htmlFor="file" className="form-label">
                        Upload File (PDF, JPEG, PNG) - Max 5MB
                      </Label>
                      <input
                        type="file"
                        className="form-control"
                        id="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                      />
                      <div className="form-text">
                        {file ? `Selected file: ${file.name}` :
                         existingFile ? `Current file: ${getFileName(existingFile)}` :
                         "No file selected"}
                      </div>
                      {existingFile && !file && (
                        <div className="mt-2">
                          <a
                            href={`${backendUrl}${existingFile}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-sm"
                          >
                            View Current File
                          </a>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? "Updating..." : "Update Refund"}
                  </Button>
                  <Button
                    color="secondary"
                    onClick={() => navigate("/course-refunds-list")}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                </div>
              </AvForm>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default EditRefund;
